/**
 * Weekly bucketing for the dashboard trends.
 *
 * Pure, and takes `now` as an argument, because a chart that shifts by one
 * column depending on when the test ran is a chart nobody can verify.
 *
 * Weeks run Monday to Sunday and are labelled by how recent they are, so W12
 * is always the week in progress. A shop reads "is it going up", not "which
 * ISO week was that".
 */

const DAY = 86_400_000;
const WEEK = 7 * DAY;

/** Midnight Monday of the week containing `t`, in UTC. */
export function weekStart(t: number): number {
  const d = new Date(t);
  const utcMidnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  // getUTCDay: 0 = Sunday. Monday-based offset makes Sunday six days in, not one.
  const offset = (d.getUTCDay() + 6) % 7;
  return utcMidnight - offset * DAY;
}

export interface WeekWindow {
  /** Monday timestamps, oldest first. */
  starts: number[];
  /** Labels for the chart axis, oldest first. */
  labels: string[];
}

export function lastWeeks(count: number, now: number): WeekWindow {
  const current = weekStart(now);
  const starts: number[] = [];
  for (let i = count - 1; i >= 0; i--) starts.push(current - i * WEEK);
  return { starts, labels: starts.map((_, i) => `W${i + 1}`) };
}

/**
 * Drop each dated item into its week. Anything outside the window is ignored
 * rather than folded into the first bucket — a two-year-old row would otherwise
 * show up as a spike in W1.
 */
export function bucketByWeek<T>(
  items: T[],
  dateOf: (item: T) => string | number | null | undefined,
  window: WeekWindow,
): T[][] {
  const buckets: T[][] = window.starts.map(() => []);
  const first = window.starts[0] ?? 0;
  for (const item of items) {
    const raw = dateOf(item);
    if (raw === null || raw === undefined) continue;
    const t = typeof raw === 'number' ? raw : Date.parse(raw);
    if (!Number.isFinite(t) || t < first) continue;
    const index = Math.floor((weekStart(t) - first) / WEEK);
    if (index >= 0 && index < buckets.length) buckets[index]!.push(item);
  }
  return buckets;
}

/**
 * Percent of decided proposals that were kept, per week.
 *
 * `null` for a week with no decisions — not zero. Zero means "everything the
 * model proposed was rejected", which is a crisis; no decisions means the shop
 * was closed. Charting them the same way would invent the crisis.
 */
export function acceptanceByWeek(
  decisions: Array<{ verdict: string; decidedAt: string | number | null }>,
  window: WeekWindow,
): Array<number | null> {
  return bucketByWeek(decisions, (d) => d.decidedAt, window).map((week) => {
    const counted = week.filter((d) => d.verdict === 'accepted' || d.verdict === 'rejected');
    if (counted.length === 0) return null;
    const kept = counted.filter((d) => d.verdict === 'accepted').length;
    return Math.round((kept / counted.length) * 100);
  });
}
